import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import Headline from '@sv-support-sdk/ui-components/src/components/Typography/Headline';

import MessageList from './components/MessageList';
import MessageForm from './components/MessageForm';
import Badge from '../../components/Feedback/Badge';
import PageContentInner from '../../components/Layout/Page/PageContentInner';
import Breadcrumbs from '../../components/Navigation/Breadcrumbs';
import Error from '../../components/Feedback/Error';
import Loader from '../../components/Feedback/Loader';

import { $l } from '../../locales';
import { routes } from '../../routing';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import ticketsActions from '../../store/actions/tickets';
import { ticketSelector, selectSelectedTicketLoading } from '../../store/selectors/tickets';
import { selectUserLoaded, userAuthorizedSelector } from '../../store/selectors/user';
import { WORKFLOW_FRONT_URL } from '../../config';
import useMobileDetect from '../../hooks/useMobileDetect';

import css from './style.module.scss';

const Ticket = () => {
  const [isFormOpened, setIsFormOpened] = useState(false);
  const { ticketId } = useParams();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const contentInnerRef = useRef(null);

  const isAuthorized = useAppSelector(userAuthorizedSelector);
  const isUserLoaded = useAppSelector(selectUserLoaded);
  const ticket = useAppSelector(ticketSelector);
  const ticketLoading = useAppSelector(selectSelectedTicketLoading);
  const isMobile = useMobileDetect();

  const hash = useMemo(() => {
    return searchParams.get('hash');
  }, [searchParams]);

  useEffect(() => {
    return () => {
      dispatch(ticketsActions.resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (ticketId && (isAuthorized || hash)) {
      dispatch(ticketsActions.getOneTicket(ticketId, hash || undefined));
    }
  }, [ticketId, dispatch, isAuthorized, hash]);

  useEffect(() => {
    if (ticket) {
      setIsFormOpened(!ticket.closedAt);
    }
  }, [ticket]);

  useEffect(() => {
    if (isUserLoaded && !isAuthorized && !hash) {
      window.location.href = `${WORKFLOW_FRONT_URL}/login?redirect=${window.location.href}`;
    }
  }, [isUserLoaded, isAuthorized, hash]);

  const breadcrumbs = useMemo(
    () => [
      { text: $l('BREADCRUMBS.SUPPORT_HISTORY'), link: routes.tickets },
      { text: `${$l('BREADCRUMBS.TICKET')} ${ticketId}`, isLast: true },
    ],
    [ticketId],
  );

  if ((ticketLoading || !isUserLoaded) && !ticket) {
    return (
      <PageContentInner>
        <Breadcrumbs items={breadcrumbs} className="indent-md-bottom" />
        <div className={css.loader}>
          <Loader />
        </div>
      </PageContentInner>
    );
  }

  if (!ticket && isAuthorized) {
    return (
      <PageContentInner>
        <Error
          title={$l('ERROR_PAGE.SOMETHING_WENT_WRONG')}
          description={$l('ERROR_PAGE.PROCESSING_PROBLEM')}
          icon="150--error-sign"
          action={{ text: $l('ERROR_PAGE.GO_TO_HOME'), onClick: () => navigate('/') }}
        />
      </PageContentInner>
    );
  }

  if (!ticket) return null;

  return (
    <PageContentInner ref={contentInnerRef} width="full" height="full">
      <Breadcrumbs items={breadcrumbs} className={`indent-${isMobile ? 'sm' : 'md'}-bottom`} />
      <List
        gap={isMobile ? '8' : '16'}
        isWrapped
        direction={isMobile ? undefined : 'row'}
        align={isMobile ? 'start' : 'center'}
      >
        <Headline>
          {$l('TICKETS.TICKET')} {ticketId}
        </Headline>
        <Badge color={ticket.status.color}>{ticket.status.children}</Badge>
      </List>
      <MessageList messages={ticket.messages} />
      <MessageForm
        isFormOpened={!ticket.closedAt || isFormOpened}
        onOpenForm={() => setIsFormOpened(true)}
        ticketId={ticketId as string}
        hash={hash || ''}
      />
    </PageContentInner>
  );
};

export default Ticket;
